import React from 'react';
import {
  Backdrop,
  Box,
  CircularProgress,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';

export type FileTransferMode = 'upload' | 'download';

export type FileTransferProgressInfo = {
  overallPercent: number | null;
  filePercent: number | null;
  fileIndex: number;
  fileCount: number;
  currentFilename: string;
};

export type FileTransferProgressProps = {
  open: boolean;
  mode: FileTransferMode;
  /** @deprecated overallPercent 사용 */
  percent?: number | null;
  /** @deprecated currentFilename 사용 */
  filename?: string | null;
  message?: string | null;
  currentFilename?: string | null;
  filePercent?: number | null;
  overallPercent?: number | null;
  /** 1-based */
  fileIndex?: number | null;
  fileCount?: number | null;
};

export function clampPct(p: number | null | undefined): number | null {
  if (typeof p !== 'number' || !Number.isFinite(p)) return null;
  return Math.max(0, Math.min(100, Math.round(p)));
}

/** 파일 크기(없으면 1) 가중치 — 전체 진행률용 */
export function fileProgressWeights(files: File[]): number[] {
  return files.map((f) => {
    const n = Number(f?.size);
    return Number.isFinite(n) && n > 0 ? n : 1;
  });
}

/**
 * 순차 전송: i번째 항목의 진행(filePct 0~100) → 전체·현재 진행률.
 * overall = (완료 가중치 + 현재 가중치 * filePct/100) / 총가중치
 */
export function progressFromSequentialItems(
  items: { name: string; sizeBytes?: number | null }[],
  fileIndex0: number,
  filePct: number | null
): FileTransferProgressInfo {
  const list = items.length ? items : [{ name: '파일', sizeBytes: 1 }];
  const fileCount = Math.max(1, list.length);
  const idx = Math.max(0, Math.min(fileIndex0, list.length - 1));
  const weights = list.map((it) => {
    const n = Number(it.sizeBytes);
    return Number.isFinite(n) && n > 0 ? n : 1;
  });
  const totalW = weights.reduce((a, b) => a + b, 0) || 1;
  const doneW = weights.slice(0, idx).reduce((a, b) => a + b, 0);
  const curW = weights[idx] ?? 1;
  const fp =
    typeof filePct === 'number' && Number.isFinite(filePct)
      ? Math.max(0, Math.min(100, filePct))
      : 0;
  const overall = ((doneW + (curW * fp) / 100) / totalW) * 100;
  return {
    overallPercent: clampPct(overall),
    filePercent: filePct == null ? null : clampPct(fp),
    fileIndex: idx + 1,
    fileCount,
    currentFilename: list[idx]?.name || `파일 ${idx + 1}`,
  };
}

/** @deprecated progressFromSequentialItems 사용 */
export function progressFromSequentialUpload(
  files: File[],
  fileIndex0: number,
  filePct: number | null
): FileTransferProgressInfo {
  return progressFromSequentialItems(
    files.map((f) => ({ name: f.name, sizeBytes: f.size })),
    fileIndex0,
    filePct
  );
}

/** 단일 multipart 업로드 진행 — 전체는 axios, 현재 파일은 크기 가중으로 추정 */
export function progressFromMultipartUpload(
  files: File[],
  loaded: number,
  total: number | undefined
): FileTransferProgressInfo {
  const list = files.filter(Boolean);
  const fileCount = Math.max(1, list.length);
  const overall =
    total && total > 0 ? clampPct((loaded * 100) / total) : null;
  if (list.length === 0) {
    return {
      overallPercent: overall,
      filePercent: overall,
      fileIndex: 1,
      fileCount: 1,
      currentFilename: '파일',
    };
  }
  const weights = fileProgressWeights(list);
  const totalW = weights.reduce((a, b) => a + b, 0) || 1;
  if (overall == null) {
    return {
      overallPercent: null,
      filePercent: null,
      fileIndex: 1,
      fileCount: list.length,
      currentFilename: list[0].name || '파일 1',
    };
  }
  const target = (overall / 100) * totalW;
  let acc = 0;
  let idx = list.length - 1;
  let within = weights[idx] || 1;
  for (let i = 0; i < list.length; i++) {
    const w = weights[i] || 1;
    if (target <= acc + w || i === list.length - 1) {
      idx = i;
      within = Math.max(0, target - acc);
      break;
    }
    acc += w;
  }
  const w = weights[idx] || 1;
  return {
    overallPercent: overall,
    filePercent: clampPct((within * 100) / w),
    fileIndex: idx + 1,
    fileCount: list.length,
    currentFilename: list[idx].name || `파일 ${idx + 1}`,
  };
}

function ProgressRow(props: {
  label: string;
  percent: number | null;
  detail?: string | null;
}) {
  const pct = clampPct(props.percent);
  return (
    <Box sx={{ mb: 1.5, textAlign: 'left' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          mb: 0.5,
          gap: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {props.label}
          {props.detail ? ` · ${props.detail}` : ''}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {pct != null ? `${pct}%` : '…'}
        </Typography>
      </Box>
      <LinearProgress
        variant={pct != null ? 'determinate' : 'indeterminate'}
        value={pct ?? 0}
        sx={{ height: 8, borderRadius: 1 }}
      />
    </Box>
  );
}

export const FileTransferProgressDialog: React.FC<FileTransferProgressProps> = ({
  open,
  mode,
  percent = null,
  filename,
  message,
  currentFilename,
  filePercent = null,
  overallPercent = null,
  fileIndex = null,
  fileCount = null,
}) => {
  const title =
    message ||
    (mode === 'upload' ? '파일 업로드 중…' : '파일 다운로드 중…');

  const displayName = (currentFilename ?? filename ?? '').trim() || null;
  const overall = clampPct(
    overallPercent != null ? overallPercent : percent
  );
  // 다중 파일에서 filePercent 미전달 시 overall로 채우면 두 막대가 같아져 보임
  const filePct = clampPct(
    filePercent != null
      ? filePercent
      : fileCount != null && fileCount <= 1
        ? overall
        : null
  );
  const idx =
    typeof fileIndex === 'number' && fileIndex > 0 ? fileIndex : null;
  const count =
    typeof fileCount === 'number' && fileCount > 0 ? fileCount : null;
  const fileLabel =
    idx != null && count != null
      ? `현재 파일 (${idx}/${count})`
      : '현재 파일';

  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: (t) => t.zIndex.modal + 10,
        color: '#fff',
        flexDirection: 'column',
        bgcolor: 'rgba(0,0,0,0.55)',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 3,
          minWidth: 340,
          maxWidth: 480,
          width: '90%',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <CircularProgress size={40} sx={{ mb: 1.5 }} />
          <Typography variant="h6">{title}</Typography>
        </Box>

        {displayName ? (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              px: 1,
              py: 1,
              bgcolor: 'action.hover',
              borderRadius: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: 600,
            }}
            title={displayName}
          >
            {displayName}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            파일명 확인 중…
          </Typography>
        )}

        <ProgressRow label={fileLabel} percent={filePct} />
        <ProgressRow
          label="전체 진행률"
          percent={overall}
          detail={
            count != null && count > 1 && idx != null
              ? `${idx}/${count}개 파일`
              : null
          }
        />
      </Paper>
    </Backdrop>
  );
};

export default FileTransferProgressDialog;
